if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.currentProductVariantId = this.dataset.productVariantId;

        // Container that holds the extra fields (your "dropdown")
        this.recipientFieldsContainer = this.querySelector('.recipient-fields');

        // Prefer robust selectors (by name), fall back to IDs.
        this.checkboxInput =
          this.querySelector(
            'input[type="checkbox"][name^="properties[__shopify_send_gift_card_to_recipient]"]'
          ) || this.querySelector(`#Recipient-checkbox-${this.sectionId}`);

        if (!this.checkboxInput) return; // don't crash the page

        this.checkboxLabel = this.querySelector('label.recipient-checkbox');

        this.hiddenControlField =
          this.querySelector(
            'input[type="hidden"][name^="properties[__shopify_send_gift_card_to_recipient]"]'
          ) || this.querySelector(`#Recipient-control-${this.sectionId}`);

        this.emailInput =
          this.querySelector('input[name="properties[Recipient email]"]') ||
          this.querySelector(`#Recipient-email-${this.sectionId}`);

        this.nameInput =
          this.querySelector('input[name="properties[Recipient name]"]') ||
          this.querySelector(`#Recipient-name-${this.sectionId}`);

        this.messageInput =
          this.querySelector('textarea[name="properties[Message]"]') ||
          this.querySelector(`#Recipient-message-${this.sectionId}`);

        this.sendonInput =
          this.querySelector('input[name="properties[Send on]"]') ||
          this.querySelector(`#Recipient-send-on-${this.sectionId}`);

        this.offsetProperty =
          this.querySelector('input[name="properties[__shopify_offset]"]') ||
          this.querySelector(`#Recipient-timezone-offset-${this.sectionId}`);

        // Enable checkbox (it is disabled in Liquid by default)
        this.checkboxInput.disabled = false;

        // Ensure control hidden field is disabled (Shopify pattern)
        if (this.hiddenControlField) this.hiddenControlField.disabled = true;

        // Set timezone offset
        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        // Error UI
        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText || '';

        /**
         * IMPORTANT:
         * Don't rely on label "for" -> it can target the wrong checkbox when IDs are duplicated
         * (sticky ATC / quick view clones). We toggle OUR checkbox manually.
         */
        if (this.checkboxLabel) {
          this.checkboxLabel.addEventListener('click', (e) => {
            // If user actually clicked the input itself, let the browser handle it.
            if (e.target === this.checkboxInput) return;

            e.preventDefault();

            // Toggle our checkbox deterministically
            this.checkboxInput.checked = !this.checkboxInput.checked;

            // Fire change so anything else listening still works
            this.checkboxInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        // React to checkbox changes (whether from label click, keyboard, or other scripts)
        this.checkboxInput.addEventListener('change', () => this.onChange());

        // --- DATEPICKER FIX (click/tap should open calendar) ---
        if (this.sendonInput) {
          const openPicker = () => {
            // focus first so mobile browsers behave better
            this.sendonInput.focus({ preventScroll: true });

            // Chrome/Edge: forces the native picker to open from JS
            if (typeof this.sendonInput.showPicker === 'function') {
              try {
                this.sendonInput.showPicker();
              } catch (e) {
                // ignore (some browsers throw if not allowed)
              }
            }
          };

          const fieldWrapper = this.sendonInput.closest('.field') || this.sendonInput.parentElement;

          // If click lands on wrapper/icon/overlay instead of input, still open
          if (fieldWrapper) {
            fieldWrapper.addEventListener('pointerdown', (e) => {
              // Don't steal interactions with other controls
              if (e.target && (e.target.closest('a, button') || e.target === this.sendonInput)) return;
              openPicker();
            });
          }

          // Also open on direct input click
          this.sendonInput.addEventListener('click', () => openPicker());

          // Keep keyboard behavior (Space/Enter)
          this.sendonInput.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              openPicker();
            }
          });
        }
        // --- END DATEPICKER FIX ---

        // Initial state
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      connectedCallback() {
        // If theme doesn't provide these, skip silently
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event?.source === 'product-form' &&
            event?.productVariantId?.toString() === this.currentProductVariantId?.toString()
          ) {
            this.resetRecipientForm();
          }
        });

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event?.data?.sectionId?.toString() === this.sectionId?.toString()) {
            this.currentProductVariantId = event.data.variant?.id?.toString();
          }
        });

        // FIX: correctly store cartError unsubscriber
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (
            event?.source === 'product-form' &&
            event?.productVariantId?.toString() === this.currentProductVariantId?.toString()
          ) {
            this.displayErrorMessage(event.message, event.errors);
          }
        });
      }

      disconnectedCallback() {
        if (typeof this.cartUpdateUnsubscriber === 'function') this.cartUpdateUnsubscriber();
        if (typeof this.variantChangeUnsubscriber === 'function') this.variantChangeUnsubscriber();
        if (typeof this.cartErrorUnsubscriber === 'function') this.cartErrorUnsubscriber();
      }

      onChange() {
        const checked = !!this.checkboxInput?.checked;

        // Show/hide the block
        if (this.recipientFieldsContainer) this.recipientFieldsContainer.hidden = !checked;

        if (checked) {
          this.enableInputFields();
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = false;
        });
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          field.disabled = true;
        });
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();

        if (!this.errorMessageWrapper || !this.errorMessage) return;

        this.errorMessageWrapper.hidden = false;

        if (typeof body === 'object' && body) {
          this.errorMessage.innerText = this.defaultErrorHeader || title || 'Error';

          Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.sectionId}`;
            const message = Array.isArray(value) ? value.join(', ') : String(value);

            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            if (errorTextElement) {
              errorTextElement.innerText = `${message}.`;
              errorMessageElement?.classList?.remove('hidden');
            }

            const inputElement = this[`${key}Input`];
            if (inputElement?.setAttribute) {
              inputElement.setAttribute('aria-invalid', 'true');
              inputElement.setAttribute('aria-describedby', errorMessageId);
            }
          });

          return;
        }

        this.errorMessage.innerText = body || title || this.defaultErrorHeader || 'Error';
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) this.errorMessageWrapper.hidden = true;
        if (this.errorMessageList) this.errorMessageList.innerHTML = '';

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.sendonInput]
          .filter(Boolean)
          .forEach((inputElement) => {
            inputElement.setAttribute('aria-invalid', 'false');
            inputElement.removeAttribute('aria-describedby');
          });
      }

      resetRecipientForm() {
        if (!this.checkboxInput) return;

        if (this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
        }

        this.clearInputFields();
        this.clearErrorMessage();
        this.onChange();
      }
    }
  );
}
